/**
 * affiliate-overview-page.tsx — Platform → Tiếp thị liên kết → Tổng quan (KPI). Route `/platform/affiliate`.
 */
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { Handshake, Loader2, MousePointerClick, RefreshCw, UserCheck, Users, Wallet } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { StatCard } from '@/components/shared/stat-card'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { formatVnd } from '@/lib/format-money'
import { platformApiError } from '@/lib/platform-client'
import { programSummary } from '@/lib/affiliate'
import { useAffiliateConfig, useAffiliateOverview, useReconcileAffiliate } from '@/hooks/use-platform-affiliate'

export function AffiliateOverviewPage() {
  const { data, isLoading, isError } = useAffiliateOverview()
  const cfg = useAffiliateConfig()
  const reconcile = useReconcileAffiliate()
  return (
    <div className="space-y-5">
      <PageHeader
        title="Tiếp thị liên kết"
        description="Đối tác giới thiệu khách dùng eCDP và nhận hoa hồng trên đơn khách thanh toán."
        actions={
          <Button
            variant="outline"
            disabled={reconcile.isPending}
            onClick={() => reconcile.mutate(undefined, {
              onSuccess: (r) => toast.success(`Đã chạy đối soát: ${r.released} dòng hết giữ, ${r.created} hoa hồng bị sót`),
              onError: (e) => toast.error(platformApiError(e)),
            })}
          >
            {reconcile.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Đối soát ngay
          </Button>
        }
      />
      {isLoading ? <Loading /> : isError || !data ? <ErrorState /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Đối tác hoạt động" value={data.partners.active} hint={`${data.partners.suspended} đang tạm khoá`} icon={Handshake} />
            <StatCard label="Lượt nhấp 30 ngày" value={data.clicks30d} icon={MousePointerClick} />
            <StatCard label="Khách được giới thiệu" value={data.referrals} hint={`${data.leadsNew} lead mới chờ xử lý`} icon={Users} />
            <StatCard label="Khách đã thanh toán" value={data.payingCustomers} hint={`Doanh thu tính HH (trước VAT): ${formatVnd(data.referredRevenue)}`} icon={UserCheck} tone="success" />
            <StatCard label="Hoa hồng đang giữ" value={formatVnd(data.commission.holding)} icon={Wallet} tone="warning" />
            <StatCard label="Hoa hồng có thể rút" value={formatVnd(data.commission.available)} hint="Gồm cả phần đã rút / chờ chi" icon={Wallet} />
            <StatCard label="Lệnh rút chờ duyệt" value={data.withdrawals.pending.count} hint={formatVnd(data.withdrawals.pending.amount)} icon={Wallet} tone="warning" />
            <StatCard
              label="Đã chi"
              value={formatVnd(data.withdrawals.paid.net)}
              hint={`${data.withdrawals.paid.count} lệnh · thuế TNCN đã khấu trừ ${formatVnd(data.withdrawals.paid.tax)}`}
              icon={Wallet}
              tone="success"
            />
          </div>
          {data.withdrawals.approved.count > 0 && (
            <Card className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
              <span>{data.withdrawals.approved.count} lệnh đã duyệt đang chờ chuyển khoản ({formatVnd(data.withdrawals.approved.amount)}).</span>
              <Button asChild size="sm" variant="outline"><Link to="/platform/affiliate/withdrawals?status=approved">Xử lý</Link></Button>
            </Card>
          )}
        </>
      )}
      {cfg.data && (
        <Card className="space-y-2 p-5 text-sm">
          <p className="font-medium">Quy tắc đang áp dụng (phiên bản {cfg.data.config.version}{cfg.data.isDefault ? ' — mặc định' : ''})</p>
          <p>{programSummary(cfg.data.config)}</p>
          {!cfg.data.config.enabled && <p className="text-destructive">Chương trình đang TẮT — không ghi nhận khách mới và không tạo hoa hồng mới.</p>}
          <Button asChild size="sm" variant="outline"><Link to="/platform/affiliate/settings">Cài đặt chương trình</Link></Button>
        </Card>
      )}
    </div>
  )
}
